import * as React from "react";
import { connect } from "react-redux";
import ISellOrderPage, { TSellOrderPage } from "./ISellOrderPage";
import ValidateAccessHOC from "@market/components/ValidateAccessHOC/ValidateAccessHOC";
import StepsHeader from "@market/components/StepsHeader/StepsHeader";
import OrderPricing from "@market/components/OrderPricing/OrderPricing";
import Summary from "@market/components/Summary/Summary";
import Header from "@appComponents/Header/Header";
import Big, { BigConstructor } from "big.js";
import {
  agreementsForOrderSell,
  riskWarningsForOrderSell,
} from "@market/components/AgreementContainer/agreements";
import AgreementContainer from "@market/components/AgreementContainer/AgreementContainer";
import {
  formatServerErrors,
  numberWithDelimiter,
} from "@features/utils/functions";
import {
  noLeadingZeros,
  displayWarnings,
  sortOrdersByPriceDesc,
} from "@market/features/helpers/functions";
import Loader from "@appComponents/Loader/Loader";
import SecondaryMarketService from "@services/SecondaryMarket.service";
import { pushNotify } from "@appComponents/Notification/Notification";
import CurrencyElement from "@appComponents/CurrencyElement/CurrencyElement";
import { CurrencyUpper } from "@market/features/const.market";
import { CallApiOnRender } from "@features/utils/reactFunctions";
import { setAssetDetails } from "@market/features/action.market";
import ConvertShares from "@market/components/ConvertShares/ConvertShares";
import ISPV from "@market/components/SPVsList/ISPV";
import OrdersList from "@market/components/OrdersList/OrdersList";
import { EordersTypes } from "@market/enums/OrdersTypes";
import StepButtons from "@appComponents/StepButtons/StepButtons";
import Confirmation from "@appComponents/Confirmation/Confirmation";
import onlyLoggedInHOC from "@appComponents/OnlyLoggedInHOC/OnlyLoggedIn";
import { MAIN_CURRENCY } from "@bundles/consts";
import PaymentDetails from "@market/components/PaymentDetails/PaymentDetails";
import { AxiosResponse } from "axios";
import {
  setToggleAutomatically,
  getDiscountFeePercent,
  getTransactionFee,
} from "@market/components/PaymentDetails/functions";
import { IPaymentDetailsState } from "@market/components/PaymentDetails/IPaymentDetails";

import * as SMAppStyle from "@market/SMAppStyle.module.scss";

export class SellOrderPage extends React.PureComponent<
  ISellOrderPage,
  TSellOrderPage
> {
  state: TSellOrderPage = {
    step: 1,
    nextStepEnable: false,
    validationMessages: [],
    loading: false,
    checks: [],
    orderedNumberOfShares: 0,
    orderedPricePerShare: 0,
    totalValue: 0,
    bft_fee: false,
    convertShares: false,
    ordersDisplayed: false,
    paymentDetails: {
      fee: 0,
      discountIsOn: false,
      toggleTouched: false,
      toggleBlocked: false,
      discountFeePercent: 0,
      bftExchange: 0,
    },
  };

  private big: BigConstructor;
  private validationMessages: { [index: string]: string } = {
    minimum_order_value: `Your requested amount is below the minimum threshold
      of ${this.props.marketSettings.minimum_order_value} USD for this asset`,
    missing_agreements: `Please accept all agreements`,
    convert_shares: "convert_shares",
    minimum_price_floor: `Your requested price per share is below the minimum threshold of
      ${this.props.spv.detailedCompanyAsset.price_floor} USD for this asset`,
    maximum_price_cap: `Your requested price per share is above the maximum threshold (Price Cap) of ${this.props.spv.detailedCompanyAsset.price_cap} USD for this asset.`,
  };

  constructor(props: ISellOrderPage) {
    super(props);
    this.big = Big();
    this.big.RM = 0;
  }

  componentDidMount() {
    SecondaryMarketService.getExchange().then((res: AxiosResponse) => {
      this.setState({
        loading: false,
        paymentDetails: {
          ...this.state.paymentDetails,
          bftExchange: res.data.usd_bft,
        },
      });
    });
  }

  calculatePaymentDetails = (
    paymentDetails: TSellOrderPage["paymentDetails"],
    totalValue: number
  ) => {
    let fee;
    const { calculatedFee, calculatedDiscountIsOn, toggleBlocked } =
      setToggleAutomatically(
        getDiscountFeePercent(true, this.props.marketSettings, true),
        paymentDetails.discountIsOn,
        paymentDetails.toggleTouched,
        paymentDetails.bftExchange,
        totalValue,
        this.props.userWallets.bftWallet.balance
      );
    const discountFeePercent = getDiscountFeePercent(
      calculatedDiscountIsOn,
      this.props.marketSettings
    );

    if (!calculatedFee) {
      fee = getTransactionFee(
        discountFeePercent,
        calculatedDiscountIsOn,
        totalValue,
        paymentDetails.bftExchange
      );
    } else {
      fee = calculatedFee;
    }

    return {
      ...paymentDetails,
      fee,
      discountFeePercent,
      toggleBlocked,
      discountIsOn: calculatedDiscountIsOn,
    };
  };

  validation = (values: {}) => {
    const tempState = { ...this.state, ...values };
    const { marketSettings } = this.props;
    const msg: string[] = [];

    switch (tempState.step) {
      case 1:
        if (
          this.big(tempState.totalValue).lt(marketSettings.minimum_order_value)
        )
          msg.push("minimum_order_value");
        if (
          this.big(Number(tempState.orderedNumberOfShares)).gt(
            this.getTradingShares()
          ) &&
          !tempState.convertShares
        ) {
          msg.push("convert_shares");
        }
        if (
          this.big(Number(tempState.orderedPricePerShare)).lt(
            this.props.spv.detailedCompanyAsset.price_floor
          )
        ) {
          msg.push("minimum_price_floor");
        }
        if (
          this.props.spv.detailedCompanyAsset.price_cap_enabled &&
          this.big(Number(tempState.orderedPricePerShare)).gt(
            this.props.spv.detailedCompanyAsset.price_cap
          )
        ) {
          msg.push("maximum_price_cap");
        }
        return msg;
      case 2:
        if (
          tempState.checks.length !==
          agreementsForOrderSell.length + riskWarningsForOrderSell.length
        ) {
          msg.push("missing_agreements");
        }
        return msg;
      default:
        return [];
    }
  };

  convertSharesValidation = (state: TSellOrderPage = this.state) => {
    return this.big(Number(state.orderedNumberOfShares)).gt(
      this.getTradingShares()
    );
  };

  updateState = (values: {
    [key: string]: Partial<TSellOrderPage | IPaymentDetailsState>;
  }) => {
    const messages = this.validation(values);
    const nextStepEnable = messages.length === 0;
    const paymentDetails =
      Object.keys(values)[0] === "paymentDetails"
        ? this.calculatePaymentDetails(
            values.paymentDetails as IPaymentDetailsState,
            this.state.totalValue
          )
        : this.calculatePaymentDetails(
            this.state.paymentDetails,
            (values.totalValue || this.state.totalValue) as number
          );

    this.setState({
      ...values,
      nextStepEnable,
      paymentDetails,
      ordersDisplayed: false,
      bft_fee: paymentDetails.discountIsOn,
      validationMessages: messages.map((msg) => this.validationMessages[msg]),
    });
  };

  renderMatchedOrders = () => {
    const { orderedPricePerShare } = this.state;
    const { spv, userInfo } = this.props;
    if (Number(orderedPricePerShare) === 0) return;

    const matchedOrders = spv.buyOrders
      ? spv.buyOrders
          .filter(
            (el) =>
              el.investor_id !== userInfo.investor_id &&
              el.status === "active" &&
              this.big(el.price).gte(orderedPricePerShare)
          )
          .sort(sortOrdersByPriceDesc)
          .slice(0, 10)
      : [];

    if (matchedOrders.length > 0) {
      this.setState({
        nextStepEnable: false,
        ordersDisplayed: true,
      });
      return (
        <OrdersList
          matchedOrders={true}
          orders={matchedOrders}
          investorId={userInfo.investor_id}
          type={EordersTypes.buy}
          spvId={this.props.spv.id}
          sellingEnabled={true}
        />
      );
    }

    return null;
  };

  displayStep = () => {
    const { spv } = this.props;
    const availableNumberOfShares = this.getAssetsShares();
    const header = [
      {
        name: "Last price on market:",
        value: (
          <CurrencyElement
            value={spv.last_trade_price || 0}
            currency={CurrencyUpper}
          />
        ),
      },
      {
        name: "Last price from your trades:",
        value: (
          <CurrencyElement
            value={spv!.orderShares!.last_investor_price || 0}
            currency={CurrencyUpper}
          />
        ),
      },
      {
        name: "Price Floor:",
        value: (
          <CurrencyElement
            value={Number(spv!.detailedCompanyAsset.price_floor) || 0}
            currency={CurrencyUpper}
          />
        ),
      },
      {
        name: "Price Cap:",
        value: (
          <CurrencyElement
            value={Number(spv!.detailedCompanyAsset.price_cap) || 0}
            currency={CurrencyUpper}
          />
        ),
      },
    ];

    switch (this.state.step) {
      case 1:
      default:
        return (
          <>
            <OrderPricing
              numberOfSharesInput={this.state.orderedNumberOfShares}
              pricePerShareInput={this.state.orderedPricePerShare}
              totalOrderValue={this.state.totalValue}
              availableNumberOfShares={availableNumberOfShares}
              updateState={this.updateState}
              header={
                spv.detailedCompanyAsset.price_cap_enabled
                  ? header
                  : header.slice(0, 3)
              }
            />
            {this.renderMatchedOrders()}
            <div className={SMAppStyle.converter}>
              {!this.state.ordersDisplayed &&
                this.convertSharesValidation() &&
                this.displaySharesConverter()}
            </div>
          </>
        );
      case 2:
        return (
          <AgreementContainer
            updateState={this.updateState}
            checks={this.state.checks}
            action={"SellOrder"}
            documentData={{
              assetId: spv.trading_asset_id,
              orderData: {
                numberOfShares: this.state.orderedNumberOfShares,
                pricePerShare: this.state.orderedPricePerShare,
              },
            }}
          />
        );
      case 3:
        return (
          <Summary
            userWallets={this.props.userWallets}
            spvName={this.props.spv.name}
            paymentDetails={this.state.paymentDetails}
            totalValue={this.state.totalValue}
            isSellPage={true}
            isOrderType={true}
          />
        );
    }
  };

  prevStep = () => {
    if (this.state.step === 1) return;

    this.setState({
      step: this.state.step - 1,
      nextStepEnable: true,
      validationMessages: [],
    });

    window.scrollTo(0, 0);
  };

  nextStep = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (this.state.step <= 2) {
      this.setState({
        step: this.state.step + 1,
        nextStepEnable:
          this.validation({ step: this.state.step + 1 }).length === 0,
      });
    } else if (this.state.step === 3) {
      this.setState(
        {
          loading: true,
        },
        () => {
          const data = {
            number_of_shares: Number(this.state.orderedNumberOfShares),
            price_per_share: Number(this.state.orderedPricePerShare),
            share_transfer_agreement:
              this.state.checks.length ===
              agreementsForOrderSell.length + riskWarningsForOrderSell.length,
            bft_fee: this.state.bft_fee,
            convert_shares: this.state.convertShares ? true : undefined,
          };

          SecondaryMarketService.postSellOrder(
            this.props.spv.trading_asset_id,
            data
          )
            .then((resp) => {
              this.setState({
                loading: false,
                step: this.state.step + 1,
              });
            })
            .catch((err) => {
              if (err.response !== undefined && err.response.status === 422) {
                pushNotify(
                  "error",
                  formatServerErrors(err.response.data.errors)
                );
              } else {
                this.props.history.push(`/error/${err.response.status}`);
              }
            });
        }
      );
    }
    window.scrollTo(0, 0);
  };

  displayButtons = () => {
    const { nextStepEnable, convertShares, step } = this.state;

    return (
      <StepButtons
        isFirstStep={step === 1}
        nextStepEnable={nextStepEnable}
        nextStep={this.nextStep}
        prevStep={this.prevStep}
        nextText={
          step === 3
            ? `Post Offer${convertShares ? " & Convert Shares" : ""}`
            : "Next"
        }
      />
    );
  };

  displaySharesConverter = () => {
    return (
      <ConvertShares
        assets={this.props.spv!.orderShares!.assets}
        value={this.state.convertShares}
        setCheckbox={this.updateState}
        type="order"
      />
    );
  };

  isSuccessPage = () => {
    return this.state.step === 4 && !this.state.loading;
  };

  conirmationData = () => {
    const link = `/companies/${this.props.spv.company_slug}/market/spvs/${this.props.spv.id}/orders`;

    return {
      title: "Your order has been posted",
      texts: [
        `${numberWithDelimiter(
          noLeadingZeros(this.state.orderedNumberOfShares)
        )} shares of ${this.props.spv.name} for `,
        <CurrencyElement
          value={this.state.orderedPricePerShare}
          currency={MAIN_CURRENCY}
        />,
        ` per share`,
      ],
      link1: {
        text: "See your offer",
        url: "/market/my_orders",
      },
      link2: {
        text: "Back to market",
        url: `${link}`,
      },
    };
  };

  displayWarnings = () => {
    if (this.state.validationMessages.includes("convert_shares")) {
      return null;
    }

    if (
      this.state.orderedNumberOfShares !== 0 &&
      this.state.orderedPricePerShare !== 0
    ) {
      return (
        <div className={SMAppStyle.warning}>
          {displayWarnings(this.state.validationMessages)}
        </div>
      );
    }

    return null;
  };

  getAssetsShares = () => {
    if (this.props.spv.orderShares) {
      return this.props.spv.orderShares.assets.reduce((prev, curr) => {
        return Number(this.big(prev).add(curr.sellable_number_of_shares));
      }, 0);
    }

    return 0;
  };

  getTradingShares = () => {
    const tradingAsset = this.props.spv!.orderShares!.assets.find(
      (el) => el.trading
    );
    return tradingAsset ? tradingAsset.sellable_number_of_shares : 0;
  };

  render() {
    if (this.state.loading) return <Loader moreSpace={true} />;

    return (
      <div>
        <Header padding={"50 0"}>
          <h1>Sell Order</h1>
        </Header>
        <div className={SMAppStyle.container}>
          {this.isSuccessPage() ? (
            <CallApiOnRender
              api={() => {
                this.props.setAssetDetails(
                  this.props.spv.trading_asset_id,
                  this.props.spv.id
                );
              }}
            >
              <Confirmation data={this.conirmationData()} />
            </CallApiOnRender>
          ) : (
            <>
              <main className={SMAppStyle.left}>
                <StepsHeader
                  steps={["Order details", "Agreements", "Summary"]}
                  current={this.state.step}
                />
                {this.displayStep()}
                {this.displayWarnings()}
                {this.displayButtons()}
              </main>
              <aside className={SMAppStyle.right}>
                <PaymentDetails
                  isSellPage={true}
                  spv={this.props.spv}
                  orderedNumberOfShares={this.state.orderedNumberOfShares}
                  orderedPricePerShare={this.state.orderedPricePerShare}
                  totalValue={this.state.totalValue}
                  updateState={this.updateState}
                  paymentDetails={this.state.paymentDetails}
                  bftFeeDiscountPercent={
                    this.props.marketSettings.bft_fee_discount_percent
                  }
                  isBlocked={this.state.step >= 3}
                />
              </aside>
            </>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (store: any, ownProps: any) => {
  return {
    spv: store.secondaryMarketReducer.spvs
      ? store.secondaryMarketReducer.spvs.filter(
          (spv: ISPV) => spv.id === Number(ownProps.match.params.asset_id)
        )[0]
      : store.secondaryMarketReducer.spvs,
    marketSettings: store.secondaryMarketReducer.marketSettings,
    userInfo: store.userInfo,
    userWallets: store.secondaryMarketReducer.userWallets,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    setAssetDetails: (assetId: number, spvId: number) => {
      dispatch(setAssetDetails(assetId, spvId));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(onlyLoggedInHOC(ValidateAccessHOC(SellOrderPage)));
